import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { CheckCircle2, Mail, UserPlus, Users, Minus, Plus, Copy, Check, Landmark, CreditCard, Heart } from "lucide-react";
import { usd, CARD_PAYMENTS_ENABLED } from "../../lib/api";

export const inputCls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm focus:border-[#008057] outline-none bg-white";
const box = "rounded-2xl bg-white border border-[#E2EBF0] p-6";
const choice = (on) => (on ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0]");

const PKG_COPY = {
  single: { tag: "Make one dream possible.", benefits: ["Full camp scholarship for 1 child", "2 VIP celebration dinner tickets", "1 commemorative Sporting Camps scarf", "Sponsor acknowledgment during camp and at the celebration dinner"] },
  community: { tag: "Double the opportunity. Double the impact.", benefits: ["Full camp scholarships for 2 children", "4 VIP celebration dinner tickets", "2 commemorative Sporting Camps scarves", "Enhanced sponsor recognition during camp and at the dinner", "Acknowledgment on Sporting Camps social media"] },
  champions: { tag: "Champion five dreams. Change five lives.", benefits: ["Full camp scholarships for 5 children", "6 VIP celebration dinner tickets", "5 commemorative Sporting Camps scarves", "Premium sponsor recognition during camp and at the dinner", "Featured acknowledgment on Sporting Camps social media", "Special recognition during the closing ceremony", "Designation as a Presenting Scholarship Sponsor for that camp"] },
};

export const SponsorHero = () => (
  <section className="bg-[#03281B] brand-stripe">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
      <div className="inline-flex items-center gap-2 text-[#F3C242] text-xs font-bold uppercase tracking-[0.2em]"><Heart size={16} /> Youth Sponsorship Program</div>
      <h1 className="font-display text-4xl sm:text-6xl font-extrabold text-white mt-3 tracking-tight">ONE OPPORTUNITY CHANGES EVERYTHING</h1>
      <p className="text-[#F3C242] font-bold uppercase tracking-wide mt-3">Support a young athlete. Empower a brighter future.</p>
      <p className="text-white/80 mt-4 max-w-2xl">Help us give more children the chance to experience the unique Sporting CP methodology, develop their skills and confidence, and be part of something bigger on and off the field.</p>
    </div>
  </section>
);

const PackageCard = ({ p, selected, onSelect }) => (
  <button type="button" data-testid={`sponsor-pkg-${p.key}`} onClick={onSelect} className={`text-left rounded-2xl border-2 p-6 transition-colors ${selected ? "border-[#008057] bg-[#F2F8F5] shadow-lg shadow-emerald-950/10" : "border-[#E2EBF0] bg-white hover:border-[#008057]/50"}`}>
    <div className="text-xs font-bold uppercase tracking-widest text-[#008057]">{p.name}</div>
    <div className="text-sm font-semibold text-[#03281B] mt-1">{PKG_COPY[p.key]?.tag}</div>
    <div className="mt-4 flex items-baseline gap-2"><span className="font-display text-4xl font-extrabold text-[#03281B]">{usd(p.price)}</span></div>
    <div className="text-xs font-bold uppercase text-[#F3C242] tracking-wide">Support {p.children} young athlete{p.children > 1 ? "s" : ""}</div>
    <ul className="mt-4 space-y-2">
      {(PKG_COPY[p.key]?.benefits || []).map((b) => (
        <li key={b} className="flex gap-2 text-sm text-slate-600"><CheckCircle2 size={16} className="text-[#008057] shrink-0 mt-0.5" />{b}</li>
      ))}
    </ul>
  </button>
);

const InfoTile = ({ icon: Icon, title, text }) => (
  <div className="rounded-xl bg-[#F2F8F5] p-4 flex gap-3"><Icon className="text-[#008057] shrink-0" /><div><div className="font-bold text-[#03281B]">{title}</div><p className="text-sm text-slate-600">{text}</p></div></div>
);

export const PackagePicker = ({ packages, pkgKey, onChoose, termsUrl }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
    <div className="font-display font-bold text-2xl text-[#03281B] mb-5">Choose your sponsorship package</div>
    <div className="grid md:grid-cols-3 gap-6">
      {packages.map((p) => <PackageCard key={p.key} p={p} selected={p.key === pkgKey} onSelect={() => onChoose(p)} />)}
    </div>
    <div className="grid sm:grid-cols-2 gap-4 mt-6">
      <InfoTile icon={UserPlus} title="Nominate a child" text="You can nominate a specific participant to sponsor." />
      <InfoTile icon={Users} title="Or let us select" text="Allow Sporting Camps to select a deserving child from our scholarship pool." />
    </div>
    <p className="text-slate-600 text-sm mt-6 flex items-center gap-2"><Mail size={16} className="text-[#008057]" /> For more information, contact us at <a data-testid="sponsor-contact-email" href="mailto:sponsorships@northcrestathletics.com" className="text-[#008057] font-semibold hover:underline">sponsorships@northcrestathletics.com</a></p>
    <p className="text-xs text-slate-400 mt-3">This is a child sponsorship initiative, not a charitable donation, and sponsorship payments are not represented as tax-deductible. Beneficiary eligibility and selection follow the official program regulations{termsUrl ? <> (<a className="underline" href={termsUrl}>view regulations</a>)</> : " (configurable link, provided separately)"}.</p>
  </section>
);

export const QuantityPicker = ({ pkg, qty, children, onChange }) => (
  <div className={box}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-3">How many {pkg.name} packages?</div>
    <div className="flex items-center gap-4">
      <button data-testid="sponsor-qty-minus" onClick={() => onChange(qty - 1)} className="w-10 h-10 rounded-full border-2 border-[#E2EBF0] flex items-center justify-center"><Minus size={18} /></button>
      <span data-testid="sponsor-qty" className="font-display text-3xl font-extrabold text-[#03281B] w-12 text-center">{qty}</span>
      <button data-testid="sponsor-qty-plus" onClick={() => onChange(qty + 1)} className="w-10 h-10 rounded-full border-2 border-[#E2EBF0] flex items-center justify-center"><Plus size={18} /></button>
      <span className="text-slate-500 text-sm ml-2">{usd(pkg.price)} each &middot; {children} child{children > 1 ? "ren" : ""} supported</span>
    </div>
  </div>
);

const NOMINEE_FIELDS = [["first_name", "Child's first name"], ["last_name", "Child's last name"], ["age", "Child's age or date of birth (if known)"], ["guardian_name", "Guardian's name (if known)"], ["guardian_contact", "Guardian's contact (if known)"], ["notes", "Additional info (optional)"]];

const NomineeForm = ({ onChange }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {NOMINEE_FIELDS.map(([k, ph]) => <input key={k} className={inputCls} placeholder={ph} onChange={(e) => onChange({ [k]: e.target.value })} />)}
    <p className="text-xs text-slate-500 sm:col-span-2">Nomination is subject to eligibility verification and approval by the organization; it does not automatically confirm the child's registration. Only the parent or authorized legal guardian may complete the child's formal registration.</p>
  </div>
);

const AllocationRow = ({ a, index, setAlloc, setNominee }) => (
  <div className="rounded-xl border border-[#E2EBF0] p-4">
    <div className="font-semibold text-[#03281B] mb-2">Child #{index + 1}</div>
    <div className="grid sm:grid-cols-2 gap-2 mb-2">
      <label className={`rounded-xl border-2 px-3 py-2 text-sm cursor-pointer ${choice(a.mode === "organization")}`}>
        <input type="radio" className="accent-[#008057] mr-2" checked={a.mode === "organization"} onChange={() => setAlloc(a.id, { mode: "organization" })} />
        Let the organization select a child
      </label>
      <label className={`rounded-xl border-2 px-3 py-2 text-sm cursor-pointer ${choice(a.mode === "nominate")}`}>
        <input type="radio" className="accent-[#008057] mr-2" checked={a.mode === "nominate"} onChange={() => setAlloc(a.id, { mode: "nominate" })} />
        I would like to nominate a child
      </label>
    </div>
    {a.mode === "organization"
      ? <p className="text-xs text-slate-500">The organization will select an eligible child in accordance with the official sponsorship program regulations, prioritizing young players who would otherwise be unable to participate.</p>
      : <NomineeForm onChange={(patch) => setNominee(a.id, patch)} />}
  </div>
);

export const AllocationList = ({ allocations, setAlloc, setNominee }) => (
  <div className={box}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-1">Beneficiary selection</div>
    <p className="text-sm text-slate-500 mb-4">For each sponsorship, nominate a specific child or let the organization select an eligible child.</p>
    <div className="space-y-4">
      {allocations.map((a, i) => <AllocationRow key={a.id} a={a} index={i} setAlloc={setAlloc} setNominee={setNominee} />)}
    </div>
  </div>
);

const IndividualFields = ({ set }) => (
  <>
    <input data-testid="sponsor-fname" className={inputCls} placeholder="First name" onChange={(e) => set({ first_name: e.target.value })} />
    <input className={inputCls} placeholder="Last name" onChange={(e) => set({ last_name: e.target.value })} />
    <input data-testid="sponsor-email" className={inputCls} placeholder="Email address" onChange={(e) => set({ email: e.target.value })} />
    <input className={inputCls} placeholder="Phone number" onChange={(e) => set({ phone: e.target.value })} />
  </>
);

const CorporateFields = ({ set }) => (
  <>
    <input className={inputCls} placeholder="Company / organization name" onChange={(e) => set({ company: e.target.value })} />
    <input className={inputCls} placeholder="Contact person's name" onChange={(e) => set({ contact_person: e.target.value })} />
    <input data-testid="sponsor-email" className={inputCls} placeholder="Business email" onChange={(e) => set({ business_email: e.target.value })} />
    <input className={inputCls} placeholder="Business phone" onChange={(e) => set({ business_phone: e.target.value })} />
    <input className={`${inputCls} sm:col-span-2`} placeholder="Billing address (if required)" onChange={(e) => set({ billing_address: e.target.value })} />
  </>
);

export const SponsorDetails = ({ type, setType, setSponsorField, ackName, setAckName, anonymous, setAnonymous }) => (
  <div className={box}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-3">Your details</div>
    <div className="flex gap-2 mb-4">
      <button onClick={() => setType("individual")} className={`flex-1 rounded-xl border-2 py-2 text-sm font-semibold ${choice(type === "individual")}`}>Individual</button>
      <button onClick={() => setType("corporate")} className={`flex-1 rounded-xl border-2 py-2 text-sm font-semibold ${choice(type === "corporate")}`}>Corporate / Organization</button>
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      {type === "individual" ? <IndividualFields set={setSponsorField} /> : <CorporateFields set={setSponsorField} />}
      <input className={`${inputCls} sm:col-span-2`} placeholder="Name for public acknowledgment (optional)" value={ackName} onChange={(e) => setAckName(e.target.value)} />
    </div>
    <label className="flex items-center gap-2 mt-4 text-sm text-slate-700 cursor-pointer">
      <input data-testid="sponsor-anonymous" type="checkbox" className="accent-[#008057] w-4 h-4" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
      I would like my sponsorship to remain anonymous.
    </label>
  </div>
);

export const VipGuests = ({ guests, seats, setGuestName }) => (
  <div className={box}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-1">VIP dinner guests ({seats} tickets)</div>
    <p className="text-sm text-slate-500 mb-3">Optional now - you can also submit these later. Closing dinner: June 13, 2027 (venue confirmed later).</p>
    <div className="grid sm:grid-cols-2 gap-2">
      {guests.map((g, i) => <input key={g.id} className={inputCls} placeholder={`Guest ${i + 1} name`} value={g.name} onChange={(e) => setGuestName(g.id, e.target.value)} />)}
    </div>
  </div>
);

const CopyLine = ({ label, value, copied, onCopy }) => (
  <div className="flex items-center justify-between"><span className="text-slate-500">{label}: <b className="text-[#03281B] font-mono">{value}</b></span><button onClick={() => onCopy(label, value)} className="text-[#008057]">{copied === label ? <Check size={14} /> : <Copy size={14} />}</button></div>
);

const SponsorZelle = ({ zelle, copied, onCopy }) => (
  <div className="mt-2 pt-3 border-t border-[#008057]/15">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Zelle / Chase QuickPay <span className="text-[#F3C242]">· fastest</span></div>
    <div className="flex items-center gap-3">
      <div className="bg-white p-2 rounded-lg border border-slate-200 shrink-0"><QRCodeCanvas data-testid="sponsor-zelle-qr" value={zelle.handle} size={82} fgColor="#03281B" /></div>
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Zelle: <b className="text-[#03281B] font-mono">{zelle.display || zelle.handle}</b></span><button onClick={() => onCopy("Zelle", zelle.handle)} className="text-[#008057]">{copied === "Zelle" ? <Check size={14} /> : <Copy size={14} />}</button></div>
        {zelle.name && <div className="text-xs text-slate-500">Recipient: {zelle.name}</div>}
        <p className="text-[11px] text-slate-400 mt-0.5">Scan or send Zelle to this number; add your reference in the memo.</p>
      </div>
    </div>
  </div>
);

const SponsorBank = ({ bank }) => {
  const [copied, setCopied] = useState("");
  const onCopy = (label, val) => { navigator.clipboard.writeText(val); setCopied(label); setTimeout(() => setCopied(""), 1200); };
  const rows = [["Bank", bank.bank], ["Account holder", bank.account_holder], ["Account number", bank.account_number], ["ACH routing", bank.routing_ach], ["Wire routing", bank.routing_wire]];
  return (
    <div className="mt-4 rounded-xl bg-[#F2F8F5] p-4 text-sm space-y-1">
      {rows.map(([l, v]) => <CopyLine key={l} label={l} value={v} copied={copied} onCopy={onCopy} />)}
      <p className="text-xs text-slate-500 pt-1">A unique reference is generated; include it in your transfer. Sponsorship stays Pending Payment until we verify funds.</p>
      {bank.zelle?.handle && <SponsorZelle zelle={bank.zelle} copied={copied} onCopy={onCopy} />}
    </div>
  );
};

export const SponsorPayment = ({ method, setMethod, bank }) => (
  <div className={box}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-3">Payment method</div>
    <div className={`grid ${CARD_PAYMENTS_ENABLED ? "sm:grid-cols-2" : "grid-cols-1"} gap-3`}>
      {CARD_PAYMENTS_ENABLED && (
        <button data-testid="sponsor-pay-stripe" onClick={() => setMethod("stripe")} className={`rounded-xl border-2 p-4 text-left ${choice(method === "stripe")}`}><CreditCard className="text-[#008057]" /><div className="font-bold mt-2">Credit Card</div><div className="text-xs text-slate-500">Secure via Stripe</div></button>
      )}
      <button data-testid="sponsor-pay-bank" onClick={() => setMethod("bank")} className={`rounded-xl border-2 p-4 text-left ${choice(method === "bank")}`}><Landmark className="text-[#008057]" /><div className="font-bold mt-2">Bank Transfer</div><div className="text-xs text-slate-500">ACH or wire</div></button>
    </div>
    {method === "bank" && <SponsorBank bank={bank} />}
  </div>
);

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between"><span className="text-white/60">{label}</span><span data-testid={testId}>{value}</span></div>
);

export const SponsorSummary = ({ pkg, qty, children, seats, total }) => (
  <div className="rounded-2xl bg-[#041E14] text-white p-6 lg:sticky lg:top-20">
    <div className="font-display font-bold text-lg">Sponsorship Summary</div>
    <div className="mt-4 space-y-2 text-sm">
      <Row label="Package" value={pkg.name} testId="sponsor-summary-package" />
      <Row label="Price per package" value={usd(pkg.price)} />
      <Row label="Packages" value={qty} />
      <Row label="Children sponsored" value={children} testId="sponsor-summary-children" />
      <Row label="VIP dinner tickets" value={seats} testId="sponsor-summary-seats" />
      <Row label="Commemorative scarves" value={pkg.scarves * qty} />
      <div className="flex justify-between text-[#F3C242] font-display font-extrabold text-2xl pt-2 border-t border-white/10 mt-2"><span>Total</span><span data-testid="sponsor-total">{usd(total)}</span></div>
    </div>
    <p className="text-white/50 text-xs mt-4">Includes full camp registration, official Sporting CP scarf, insurance and training kit for each sponsored child. Optional services are not included.</p>
  </div>
);
